import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, Info, Calculator as CalcIcon, Sparkles, Plus, Minus, Bath, DoorClosed, PaintBucket } from "lucide-react";
import { BHK_PRICES } from "@/components/site/RateCard";
import { getAddons } from "@/lib/content";
import { Reveal } from "@/components/site/motion";
import { motion } from "framer-motion";

const BHKS = [
  { id: "1", label: "1 BHK", note: "≈ 500–700 sq.ft" },
  { id: "2", label: "2 BHK", note: "≈ 900–1,100 sq.ft" },
  { id: "3", label: "3 BHK", note: "≈ 1,200–1,500 sq.ft" },
];

const TIERS = [
  {
    id: "tractor",
    name: "Tractor",
    full: "Tractor Emulsion",
    desc: "Economy · quick refresh",
    perks: ["2 coats paint", "Standard finish", "Quick 2-day refresh"],
  },
  {
    id: "premium",
    name: "Premium",
    full: "Premium Emulsion",
    desc: "Premium · matte / satin",
    perks: ["2 coats paint", "Low-VOC"],
    featured: true,
  },
  {
    id: "royal",
    name: "Royale",
    full: "Royale Emulsion",
    desc: "Luxury · lifetime wash",
    perks: ["2 coats paint", "Lifetime wash finish"],
  },
];

const FALLBACK_ADDONS = [
  { id: "cleaning", label: "Bathroom / Balcony cleaning", price: 2500, per: "flat", icon: "Bath", order: 1 },
  { id: "door_polish", label: "Main door hand polish", price: 2500, per: "door", icon: "DoorClosed", order: 2 },
  { id: "enamel", label: "Enamel paint", price: 800, per: "door", icon: "PaintBucket", order: 3 },
];

const ADDON_ICONS = { Bath, DoorClosed, PaintBucket, Sparkles };

const inr = (n) =>
  "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n));

export default function Calculator({ onRequestQuote }) {
  const [bhk, setBhk] = useState("2");
  const [tier, setTier] = useState("premium");
  const [addonDefs, setAddonDefs] = useState(FALLBACK_ADDONS);
  const [qty, setQty] = useState({});

  useEffect(() => {
    getAddons()
      .then((list) => {
        const act = (list || []).filter((a) => a.active !== false);
        if (act.length) setAddonDefs(act);
      })
      .catch(() => {});
  }, []);

  const selectedTier = TIERS.find((t) => t.id === tier);
  const addonsTotal = useMemo(
    () => addonDefs.reduce((s, a) => s + (qty[a.id] || 0) * a.price, 0),
    [addonDefs, qty]
  );
  const total = useMemo(() => BHK_PRICES[bhk][tier] + addonsTotal, [bhk, tier, addonsTotal]);
  const lower = Math.round(total * 0.95);
  const upper = Math.round(total * 1.15);

  const selectedAddons = addonDefs.filter((a) => (qty[a.id] || 0) > 0);
  const extrasLabel = selectedAddons
    .map((a) => (a.per === "door" ? `${a.label} x${qty[a.id]}` : a.label))
    .join(", ");

  const toggleFlat = (id) => setQty((p) => ({ ...p, [id]: p[id] ? 0 : 1 }));
  const bump = (id, d) =>
    setQty((p) => ({ ...p, [id]: Math.max(0, Math.min(10, (p[id] || 0) + d)) }));

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="py-20 md:py-28 bg-gradient-to-b from-white to-orange-50/40"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-start">
          {/* Left intro */}
          <div className="lg:sticky lg:top-28">
            <Reveal className="text-xs font-bold tracking-[0.22em] uppercase text-orange-600">
              Live cost estimator
            </Reveal>
            <Reveal as="h2" delay={0.05} className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink">
              Pick a size, pick a paint. Get your price.
            </Reveal>
            <Reveal delay={0.1} className="mt-4 text-ink-2">
              Flat all-inclusive packages — no per-square-foot maths. We give you the
              final number upfront so you can plan with confidence.
            </Reveal>

            <Reveal delay={0.15} as="ul" className="mt-6 space-y-2.5">
              {[
                "Instant flat pricing",
                "Includes labour, paint &amp; primer",
                "Final price confirmed after free site visit",
              ].map((l) => (
                <li key={l} className="flex items-center gap-2.5 text-sm text-ink-2">
                  <span className="h-5 w-5 rounded-full bg-orange-100 grid place-items-center">
                    <Check className="h-3 w-3 text-orange-700" />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: l }} />
                </li>
              ))}
            </Reveal>
          </div>

          {/* Right control room */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white hairline shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-5 md:p-8"
          >
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-orange-50 grid place-items-center">
                <CalcIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="font-display font-bold text-ink">Cost Calculator</div>
                <div className="text-xs text-muted-ink">Flat-rate estimate</div>
              </div>
            </div>

            {/* BHK selector */}
            <div className="mt-6">
              <Label className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">
                Home size
              </Label>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="calculator-bhk-row">
                {BHKS.map((b) => {
                  const active = bhk === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => setBhk(b.id)}
                      data-testid={`calc-bhk-${b.id}`}
                      className={`rounded-2xl py-4 text-center transition-all border ${
                        active
                          ? "border-orange-500 bg-orange-50/60 shadow-[0_6px_22px_rgba(249,115,22,0.20)]"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="font-display font-extrabold text-xl text-ink">{b.label}</div>
                      <div className="text-[11px] text-muted-ink mt-0.5">{b.note}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Paint tier */}
            <div className="mt-6">
              <Label className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">
                Paint type
              </Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {TIERS.map((t) => {
                  const active = tier === t.id;
                  const price = BHK_PRICES[bhk][t.id];
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTier(t.id)}
                      data-testid={`calc-tier-${t.id}`}
                      className={`text-left rounded-2xl p-4 transition-all border ${
                        active
                          ? "border-orange-500 bg-orange-50/60 shadow-[0_6px_22px_rgba(249,115,22,0.20)]"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display font-bold text-ink">{t.name}</div>
                        {t.featured && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 rounded-full px-2 py-0.5">
                            Popular
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 font-display font-black text-xl text-ink">
                        {inr(price)}
                      </div>
                      <div className="mt-1 text-xs text-muted-ink">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Extra services */}
            <div className="mt-6">
              <Label className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">
                Extra services
              </Label>
              <div className="mt-2 space-y-2" data-testid="calculator-addons">
                {addonDefs.map((a) => {
                  const Icon = ADDON_ICONS[a.icon] || Sparkles;
                  const active = (qty[a.id] || 0) > 0;
                  return (
                    <div
                      key={a.id}
                      data-testid={`calc-addon-${a.id}`}
                      className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition-all ${
                        active
                          ? "border-orange-500 bg-orange-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`h-8 w-8 rounded-xl grid place-items-center shrink-0 ${
                            active ? "bg-orange-100" : "bg-slate-100"
                          }`}
                        >
                          <Icon className={`h-4 w-4 ${active ? "text-orange-600" : "text-slate-500"}`} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-ink truncate">{a.label}</div>
                          <div className="text-[11px] text-muted-ink">
                            {inr(a.price)}{a.per === "door" ? " / door" : " flat"}
                          </div>
                        </div>
                      </div>
                      {a.per === "flat" ? (
                        <button
                          onClick={() => toggleFlat(a.id)}
                          data-testid={`calc-addon-${a.id}-toggle`}
                          aria-pressed={!!qty[a.id]}
                          className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${
                            qty[a.id] ? "bg-orange-500" : "bg-slate-200"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${
                              qty[a.id] ? "left-[22px]" : "left-0.5"
                            }`}
                          />
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => bump(a.id, -1)}
                            disabled={!qty[a.id]}
                            data-testid={`calc-addon-${a.id}-minus`}
                            aria-label={`Remove one ${a.label}`}
                            className="h-7 w-7 rounded-full border border-slate-200 grid place-items-center text-ink transition-colors disabled:opacity-30 hover:border-orange-400"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span
                            className="w-5 text-center font-display font-bold text-ink tabular-nums"
                            data-testid={`calc-addon-${a.id}-qty`}
                          >
                            {qty[a.id] || 0}
                          </span>
                          <button
                            onClick={() => bump(a.id, 1)}
                            data-testid={`calc-addon-${a.id}-plus`}
                            aria-label={`Add one ${a.label}`}
                            className="h-7 w-7 rounded-full border border-slate-200 grid place-items-center text-ink transition-colors hover:border-orange-400"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Result */}
            <div className="mt-7 rounded-2xl bg-ink text-white p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-orange-500/20 blur-3xl" />
              <div className="relative flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-orange-300">
                    Estimated Cost
                  </div>
                  <div
                    className="mt-1 font-display font-black text-4xl md:text-5xl price-roll"
                    data-testid="calculator-total-price"
                  >
                    {inr(total)}
                  </div>
                  <div className="mt-2 text-sm text-white/70">
                    Range: {inr(lower)} – {inr(upper)} after site visit
                  </div>
                  <div className="mt-2 text-xs text-orange-200 font-bold">
                    {bhk} BHK · {selectedTier.full}
                  </div>
                  {addonsTotal > 0 && (
                    <div className="mt-1 text-xs text-white/60" data-testid="calculator-addons-total">
                      Includes extra services worth {inr(addonsTotal)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 items-stretch min-w-[200px]">
                  <Button
                    onClick={() =>
                      onRequestQuote?.({
                        project_type: `${bhk} BHK · ${selectedTier.full}`,
                        area: `${bhk} BHK`,
                        estimated_cost: total,
                        message: extrasLabel ? `Extras: ${extrasLabel}` : "",
                      })
                    }
                    data-testid="calculator-request-quote"
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full h-12"
                  >
                    Request Final Quote
                  </Button>
                  <div className="text-[11px] text-white/60 flex items-start gap-1.5 leading-snug">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-orange-300" />
                    Final price may vary after site inspection.
                  </div>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-white/10">
                {selectedTier.perks.map((p) => (
                  <div key={p} className="text-xs text-white/80 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-orange-300" />
                    {p}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
